// Base local (documento 07, "Datos locales"). Todo lo que hace el chofer se escribe acá primero.
import Dexie, { type EntityTable } from 'dexie';
import type { ComprobanteImprimible, TipoOperacion } from '@reparto/shared/nucleo';
import type { ArchivoChat, ConversacionChat, MarcaChat, MensajeChat, PendienteChat } from '../chat/tipos';
import type { MovilLista, Paquete, Usuario } from './tipos';

export interface Sesion {
  id: 'actual';
  access_token: string;
  refresh_token: string;
  dispositivo_id: string;
  usuario: Usuario;
  empresa: { id: string; nombre: string };
  /** Móvil elegido hoy y fecha del paquete. */
  movil_id: string | null;
  fecha: string | null;
}

export type EstadoOperacion = 'pendiente' | 'enviando' | 'aplicada' | 'rechazada' | 'descartada';

export interface OperacionLocal {
  orden?: number;
  operacion_id: string;
  tipo: TipoOperacion;
  payload: Record<string, unknown>;
  hora_dispositivo: string;
  estado: EstadoOperacion;
  intentos: number;
  codigo?: string;
  detalle?: string;
  respuesta?: unknown;
  /** Fotos y firmas guardadas en `archivos` que hay que subir antes de enviar la operación. */
  archivos_locales?: string[];
  aplicada_en?: string;
  creado_en: string;
}

export interface ArchivoLocal {
  id: string;
  blob: Blob;
  tipo: 'firma' | 'foto_entrega';
  mime: 'image/png' | 'image/jpeg';
  /** La parada de la entrega; sin parada, la firma del "Recibí conforme" de la carga (decisión D54). */
  parada_id?: string;
  ancho: number;
  alto: number;
  tomado_en: string;
  estado: 'local' | 'subido' | 'fallido';
  archivo_id?: string;
  intentos: number;
}

export interface PosicionLocal {
  id?: number;
  movil_id: string;
  lat: number;
  lng: number;
  precision_m: number;
  velocidad_kmh?: number;
  rumbo?: number;
  hora_dispositivo: string;
  bateria_pct?: number;
}

export interface Guardado<T> {
  id: string;
  valor: T;
  guardado_en: string;
}

/** Un código leído con la cámara al verificar la carga (P2). */
export interface LecturaCodigo {
  id: string;
  /** Código normalizado, tal como se buscó. */
  codigo: string;
  /** null: el código no es de ningún artículo de esta carga. */
  articulo_id: string | null;
  /** Unidades que representa el código (la caja entera vale las unidades por bulto). 0 si es desconocido. */
  unidades: number;
  hora: string;
}

/**
 * Lo escaneado de la carga de un móvil y fecha. Se guarda en el celular (no en memoria) para que no se pierda si
 * se bloquea la pantalla, se cambia de aplicación o el sistema cierra la pestaña para liberar memoria.
 */
export interface EscaneoCarga {
  /** `${movil_id}|${fecha}` */
  id: string;
  movil_id: string;
  fecha: string;
  lecturas: LecturaCodigo[];
  actualizado_en: string;
}

/**
 * Lo que no subió al confirmar la carga, con su motivo. El servidor deja de mandar la carga cuando hay ruta, así
 * que para mirarla después (solo lectura) se guarda acá al confirmar.
 */
export interface CargaConfirmada {
  /** `${movil_id}|${fecha}` */
  id: string;
  movil_id: string;
  fecha: string;
  confirmada_en: string;
  no_cargados: Array<{
    pedido_id: string;
    numero_visible: string;
    cliente: string;
    zona: string | null;
    bultos_total: number;
    motivo: string;
  }>;
}

/** Los números de una reserva que este celular usó y el servidor todavía no confirmó (decisión D57). */
export interface ReservaUsada {
  /** El id de la reserva. */
  id: string;
  usados: number[];
}

/** El documento de un pedido para reimprimirlo: el que armó el celular o el definitivo del servidor. */
export interface ComprobanteLocal {
  pedido_id: string;
  comprobante: ComprobanteImprimible;
  /** true: lo armó el celular (puede ser provisorio); false: es el del servidor. */
  local: boolean;
}

export class BaseLocal extends Dexie {
  sesion!: EntityTable<Sesion, 'id'>;
  paquete!: EntityTable<Guardado<Paquete>, 'id'>;
  moviles!: EntityTable<Guardado<MovilLista[]>, 'id'>;
  cola!: EntityTable<OperacionLocal, 'orden'>;
  archivos!: EntityTable<ArchivoLocal, 'id'>;
  posiciones!: EntityTable<PosicionLocal, 'id'>;
  escaneos!: EntityTable<EscaneoCarga, 'id'>;
  cargas!: EntityTable<CargaConfirmada, 'id'>;
  chatConversaciones!: EntityTable<ConversacionChat, 'id'>;
  chatMensajes!: EntityTable<MensajeChat, 'id'>;
  chatPendientes!: EntityTable<PendienteChat, 'orden'>;
  chatArchivos!: EntityTable<ArchivoChat, 'id'>;
  chatMarcas!: EntityTable<MarcaChat, 'id'>;
  reservasUsadas!: EntityTable<ReservaUsada, 'id'>;
  comprobantes!: EntityTable<ComprobanteLocal, 'pedido_id'>;

  constructor(nombre = 'reparto-chofer') {
    super(nombre);
    this.version(1).stores({
      sesion: 'id',
      paquete: 'id',
      moviles: 'id',
      cola: '++orden, &operacion_id, estado',
      archivos: 'id, estado, parada_id',
      posiciones: '++id',
    });
    // Versión 2: verificación de la carga con la cámara y la carga confirmada para mirarla en solo lectura.
    this.version(2).stores({ escaneos: 'id', cargas: 'id' });
    // Versión 3: chat (decisión D39). Conversaciones, los últimos mensajes de cada una, la bandeja de salida (en orden,
    // con el id del mensaje único), fotos y notas de voz, y marcas sueltas (`desde` de novedades, presencia, push).
    this.version(3).stores({
      chatConversaciones: 'id',
      chatMensajes: 'id, [conversacion_id+seq]',
      chatPendientes: '++orden, &id, conversacion_id',
      chatArchivos: 'id, archivo_id, guardado_en',
      chatMarcas: 'id',
    });
    // Versión 4: facturación (decisión D57). Los números de la reserva que se usaron sin señal y el documento de cada
    // pedido para reimprimirlo.
    this.version(4).stores({ reservasUsadas: 'id', comprobantes: 'pedido_id' });
  }
}

export const db = new BaseLocal();
